import React, { useState, useEffect, useMemo } from 'react';
import { Star, MapPin, Languages, Briefcase, Zap, CheckCircle, Users, Clock } from 'lucide-react';
import Button from '../../../components/ui/Button';
import SelectUI from '../../../components/ui/Select';
import { notaryService } from '../../../services/notaryService';


// ─── Abidjan Communes ─────────────────────────────────────────────────────────
const ABIDJAN_COMMUNES = [
  'Abobo', 'Adjamé', 'Attécoubé', 'Cocody', 'Koumassi',
  'Marcory', 'Plateau', 'Port-Bouët', 'Treichville', 'Yopougon',
  'Bingerville', 'Anyama', 'Songon', 'Dabou',
];

const SPECIALIZATIONS = [
  'Vente immobilière',
  'Succession & héritage',
  'Droit de la famille',
  'Droit commercial',
  'Investissement immobilier',
  'Droit foncier',
  'Copropriété',
];

const LANGUAGES = ['Français', 'Anglais', 'Dioula', 'Baoulé', 'Arabe'];

// ─── Matching Algorithm ───────────────────────────────────────────────────────
const scoreNotary = (notary, criteria) => {
  let score = 0;
  const reasons = [];

  // Specialization match (40 pts)
  if (criteria?.specialization) {
    const notarySpecs = notary?.specializations || [notary?.specialization] || [];
    if (notarySpecs?.some((s) => s?.toLowerCase()?.includes(criteria?.specialization?.toLowerCase()))) {
      score += 40;
      reasons?.push({ icon: 'Briefcase', text: 'Spécialisation correspondante', pts: 40 });
    }
  }

  // Language match (25 pts)
  if (criteria?.language) {
    const notaryLangs = notary?.languages || ['Français'];
    if (notaryLangs?.includes(criteria?.language)) {
      score += 25;
      reasons?.push({ icon: 'Languages', text: `Parle ${criteria?.language}`, pts: 25 });
    }
  }

  // Commune coverage (20 pts)
  if (criteria?.commune) {
    const coverage = notary?.commune_coverage || [notary?.commune] || [];
    if (coverage?.includes(criteria?.commune)) {
      score += 20;
      reasons?.push({ icon: 'MapPin', text: `Couvre ${criteria?.commune}`, pts: 20 });
    } else if (notary?.office_address?.toLowerCase()?.includes(criteria?.commune?.toLowerCase())) {
      score += 10;
      reasons?.push({ icon: 'MapPin', text: 'Bureau proche', pts: 10 });
    }
  }

  // Caseload (15 pts — lower is better)
  const caseload = notary?.current_caseload ?? notary?.active_cases ?? Math.floor(Math.random() * 20);
  if (caseload < 5) {
    score += 15;
    reasons?.push({ icon: 'Clock', text: 'Disponibilité élevée', pts: 15 });
  } else if (caseload < 10) {
    score += 8;
    reasons?.push({ icon: 'Clock', text: 'Disponibilité modérée', pts: 8 });
  }

  // Rating bonus (up to 10 pts)
  const rating = notary?.rating || notary?.average_rating || 4.0;
  const ratingBonus = Math.round((rating - 3) * 5);
  if (ratingBonus > 0) {
    score += ratingBonus;
    reasons?.push({ icon: 'Star', text: `Note ${rating?.toFixed(1)}/5`, pts: ratingBonus });
  }

  // Verified bonus (5 pts)
  if (notary?.is_verified || notary?.verified) {
    score += 5;
    reasons?.push({ icon: 'CheckCircle', text: 'Notaire vérifié', pts: 5 });
  }

  return { score: Math.min(score, 100), reasons, caseload };
};

// ─── Match Score Badge ────────────────────────────────────────────────────────
const MatchBadge = ({ score }) => {
  const color =
    score >= 80 ? 'bg-green-100 text-green-700 border-green-200' :
    score >= 60 ? 'bg-blue-100 text-blue-700 border-blue-200' :
    score >= 40 ? 'bg-amber-100 text-amber-700 border-amber-200': 'bg-gray-100 text-gray-600 border-gray-200';
  const label =
    score >= 80 ? 'Excellent match' :
    score >= 60 ? 'Bon match' :
    score >= 40 ? 'Match partiel' : 'Match faible';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${color}`}>
      <Zap size={10} />
      {score}% — {label}
    </span>
  );
};

// ─── Notary Card ──────────────────────────────────────────────────────────────
const NotaryMatchCard = ({ notary, score, reasons, caseload, isTop, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const name = notary?.notary_name || notary?.name || notary?.user_profile?.full_name || 'Notaire';
  const rating = notary?.rating || notary?.average_rating || 4.0;
  const langs = notary?.languages || ['Français'];
  const specs = notary?.specializations || (notary?.specialization ? [notary?.specialization] : ['Droit immobilier']);
  const communes = notary?.commune_coverage || (notary?.commune ? [notary?.commune] : []);

  return (
    <div
      className={`bg-card border rounded-xl overflow-hidden transition-all duration-200 ${
        isTop
          ? 'border-green-300 shadow-md ring-1 ring-green-200'
          : 'border-border hover:border-primary/30 hover:shadow-sm'
      }`}
    >
      {isTop && (
        <div className="flex items-center gap-1.5 px-4 py-1.5 bg-green-50 border-b border-green-200">
          <Zap size={12} className="text-green-600" />
          <span className="text-xs font-bold text-green-700">Meilleure correspondance</span>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="w-12 h-12 rounded-xl bg-brand-earth/10 flex items-center justify-center flex-shrink-0 text-brand-earth font-bold text-lg">
            {name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <p className="text-sm font-bold text-foreground">{name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{specs?.[0]}</p>
              </div>
              <MatchBadge score={score} />
            </div>

            {/* Quick stats */}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Star size={11} className="text-amber-400 fill-amber-400" />
                {rating?.toFixed(1)}
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock size={11} />
                {caseload} dossiers actifs
              </span>
              {notary?.office_address && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground truncate max-w-[140px]">
                  <MapPin size={11} />
                  {notary?.office_address}
                </span>
              )}
            </div>

            {/* Languages */}
            <div className="flex items-center gap-1 mt-2 flex-wrap">
              {langs?.slice(0, 3)?.map((l) => (
                <span key={l} className="text-[10px] bg-blue-50 text-blue-600 border border-blue-100 px-1.5 py-0.5 rounded-md font-medium">
                  {l}
                </span>
              ))}
              {communes?.slice(0, 2)?.map((c) => (
                <span key={c} className="text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-100 px-1.5 py-0.5 rounded-md font-medium">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Match reasons (expandable) */}
        {reasons?.length > 0 && (
          <button
            onClick={() => setExpanded((p) => !p)}
            className="mt-3 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>{expanded ? 'Masquer' : 'Voir'} les critères de correspondance</span>
            <span className={`transition-transform ${expanded ? 'rotate-180' : ''}`}>▾</span>
          </button>
        )}
        {expanded && (
          <div className="mt-2 space-y-1">
            {reasons?.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-surface/60 rounded-lg px-3 py-1.5">
                <span className="text-text-secondary">{r?.text}</span>
                <span className="font-bold text-brand-earth">+{r?.pts} pts</span>
              </div>
            ))}
          </div>
        )}

        {/* Action */}
        <div className="mt-3 flex gap-2">
          <Button
            variant={isTop ? 'primary' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={() => onSelect(notary)}
          >
            {isTop ? 'Sélectionner ce notaire' : 'Choisir'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const IntelligentNotaryMatcher = ({ service, onNotarySelect, onBack }) => {
  const [notaries, setNotaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criteria, setCriteria] = useState({
    specialization: '',
    language: '',
    commune: '',
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await notaryService?.getAllNotaries({ is_verified: true });
        setNotaries(data || []);
      } catch {
        setNotaries([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const scored = useMemo(() => {
    return notaries?.map((n) => {
        const { score, reasons, caseload } = scoreNotary(n, criteria);
        return { notary: n, score, reasons, caseload };
      })?.sort((a, b) => b?.score - a?.score);
  }, [notaries, criteria]);

  const hasCriteria = criteria?.specialization || criteria?.language || criteria?.commune;

  const specOptions = [
    { value: '', label: 'Toutes les spécialisations' },
    ...SPECIALIZATIONS?.map((s) => ({ value: s, label: s })),
  ];
  const langOptions = [
    { value: '', label: 'Toutes les langues' },
    ...LANGUAGES?.map((l) => ({ value: l, label: l })),
  ];
  const communeOptions = [
    { value: '', label: 'Toutes les communes' },
    ...ABIDJAN_COMMUNES?.map((c) => ({ value: c, label: c })),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="outline" size="sm" iconName="ArrowLeft" iconPosition="left" onClick={onBack}>
          Retour
        </Button>
        <div>
          <h2 className="text-2xl font-heading font-semibold text-foreground">
            Matching Intelligent de Notaires
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Notre algorithme sélectionne les notaires les plus adaptés à votre situation
          </p>
        </div>
      </div>

      {/* Algorithm criteria */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={16} className="text-primary" />
          <p className="text-sm font-bold text-foreground">Critères de matching</p>
          <span className="text-xs text-muted-foreground ml-auto">
            Score calculé sur spécialisation, langue, commune et charge de travail
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SelectUI
            label="Spécialisation requise"
            options={specOptions}
            value={criteria?.specialization}
            onChange={(v) => setCriteria((p) => ({ ...p, specialization: v }))}
          />
          <SelectUI
            label="Langue préférée"
            options={langOptions}
            value={criteria?.language}
            onChange={(v) => setCriteria((p) => ({ ...p, language: v }))}
          />
          <SelectUI
            label="Commune concernée"
            options={communeOptions}
            value={criteria?.commune}
            onChange={(v) => setCriteria((p) => ({ ...p, commune: v }))}
          />
        </div>

        {/* Score legend */}
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <p className="text-xs text-muted-foreground font-semibold">Pondération :</p>
          {[
            { label: 'Spécialisation', pts: '40 pts', color: 'text-purple-600' },
            { label: 'Langue', pts: '25 pts', color: 'text-blue-600' },
            { label: 'Commune', pts: '20 pts', color: 'text-emerald-600' },
            { label: 'Disponibilité', pts: '15 pts', color: 'text-amber-600' },
          ]?.map((item) => (
            <span key={item?.label} className="flex items-center gap-1 text-xs">
              <span className={`font-bold ${item?.color}`}>{item?.pts}</span>
              <span className="text-muted-foreground">{item?.label}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Auto-suggestion banner */}
      {hasCriteria && scored?.length > 0 && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <Zap size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">
              Suggestion automatique : {scored?.[0]?.notary?.notary_name || scored?.[0]?.notary?.name || 'Notaire'}
            </p>
            <p className="text-xs text-green-700 mt-0.5">
              Score de correspondance : {scored?.[0]?.score}% — basé sur vos critères sélectionnés
            </p>
          </div>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Analyse des notaires disponibles…</p>
        </div>
      ) : scored?.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Users size={32} className="text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aucun notaire disponible pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">
              {scored?.length} notaire{scored?.length > 1 ? 's' : ''} classé{scored?.length > 1 ? 's' : ''} par pertinence
            </p>
            {!hasCriteria && (
              <p className="text-xs text-muted-foreground">
                Affinez les critères pour un matching personnalisé
              </p>
            )}
          </div>
          {scored?.map(({ notary, score, reasons, caseload }, idx) => (
            <NotaryMatchCard
              key={notary?.id}
              notary={notary}
              score={score}
              reasons={reasons}
              caseload={caseload}
              isTop={idx === 0 && hasCriteria}
              onSelect={onNotarySelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default IntelligentNotaryMatcher;
